import { useCallback, type FunctionComponent } from 'react';
import { useIntl } from 'react-intl';

import GridIcon from '@atlaskit/icon/core/grid';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import { Analytics } from '@trello/atlassian-analytics';
import { boardSwitcherSharedState } from '@trello/board-switcher';
import { Button } from '@trello/nachos/button';
import { Tooltip } from '@trello/nachos/tooltip';
import { useSharedStateSelector } from '@trello/shared-state';

import * as styles from './ToggleLayoutButton.module.less';

export const ToggleLayoutButton: FunctionComponent = () => {
  const intl = useIntl();
  const layout = useSharedStateSelector(
    boardSwitcherSharedState,
    useCallback((value) => value.layout, []),
  );

  const toggleLayout = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName:
        layout === 'grid'
          ? 'boardSwitcherListViewButton'
          : 'boardSwitcherGridViewButton',
      source: 'boardSwitcherModal',
    });

    boardSwitcherSharedState.setValue({
      layout: layout === 'grid' ? 'list' : 'grid',
    });
  }, [layout]);

  const label =
    layout === 'grid'
      ? intl.formatMessage({
          id: 'templates.board_switcher.list-view',
          defaultMessage: 'List view',
          description: 'List view button label',
        })
      : intl.formatMessage({
          id: 'templates.board_switcher.grid-view',
          defaultMessage: 'Grid view',
          description: 'Grid view button label',
        });
  const gridIcon = <GridIcon label={label} size="medium" />;
  const listIcon = <ListBulletedIcon label={label} size="medium" />;

  return (
    <Tooltip content={label} hideTooltipOnClick>
      <Button
        className={styles.button}
        iconBefore={layout === 'grid' ? listIcon : gridIcon}
        aria-label={label}
        onClick={toggleLayout}
      />
    </Tooltip>
  );
};
